import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbArrowsExchange, TbCreditCard, TbHeart, TbHeartBroken } from 'react-icons/tb';
import { BiInfoCircle } from 'react-icons/bi';
import { HiCalculator } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/buttons/Button';
import PaymentMethodIcon from '../../components/misc/PaymentMethodIcon';
import useActiveBusinessSubscription from '../../../hooks/business/useActiveBusinessSubscription';
import {
  businessBaseSubscriptionPrices,
  businessVariableSubscriptionPrices,
  communitySubscriptionPrices,
} from '../../../utils/BusinessFeatureUtils';
import { InvoiceData, PaymentDetails } from '../../../types/payments';
import { MemberCommunityDto } from '../../../types/community';
import { SubscriptionType } from '../../../types/misc';
import InvoicesTable from '../../components/tables/InvoicesTable';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import businessService from '../../../services/business/businessService';
import { businessSubscriptionService } from '../../../services/business/subscriptionService';
import PageHeader from '../../components/misc/PageHeader';
import TrialBanner from '../../components/misc/TrialBanner';
import { shouldShowTrialBanner, FREE_MEMBERS_INCLUDED } from '../../../utils/subscriptionUtils';
import { formatDate } from '../../../utils/stringUtils';
import CostCalculatorModal from '../../components/modals/CostCalculatorModal';
import AreYouSureModal from '../../components/modals/AreYouSureModal';

interface PageData {
  paymentDetails: PaymentDetails | null;
  invoices: InvoiceData[];
  memberCount: number;
  subCommunities: MemberCommunityDto[];
  endDate: Date | null;
}

function useSubscriptionPageData(deps: readonly unknown[]) {
  const [data, setData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentDetails, invoices, usage, endDateRaw] = await Promise.all([
        businessSubscriptionService.getPaymentDetails().catch(() => null),
        businessSubscriptionService.getLastInvoices(5).catch(() => [] as InvoiceData[]),
        businessSubscriptionService.getSubscriptionUsage(),
        businessSubscriptionService.getEndDate().catch(() => null),
      ]);
      setData({
        paymentDetails,
        invoices,
        memberCount: usage.memberCount,
        subCommunities: usage.subCommunities,
        endDate: endDateRaw != null ? new Date((endDateRaw as number) * 1000) : null,
      });
    } catch {
      setData({ paymentDetails: null, invoices: [], memberCount: 0, subCommunities: [], endDate: null });
    } finally {
      setLoading(false);
    }
  }, [...deps]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { data, loading };
}

function getPaidSubCommunities(communities: MemberCommunityDto[]): MemberCommunityDto[] {
  return communities.filter((c) => {
    if (!c.paidByBusiness) return false;
    const tier = c.subscription?.type ?? c.trialType;
    return tier != null && tier !== SubscriptionType.FREE;
  });
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl bg-secondary-200 bg-opacity-50 p-6 ${className}`}>
      {children}
    </div>
  );
}

interface SubscriptionStatusCardProps {
  businessSubscription: ReturnType<typeof useActiveBusinessSubscription>;
  business: ReturnType<typeof useSelectedBusiness>;
  calculatedTotal: number;
  extraMembers: number;
  basePrice: number;
  variablePrice: number;
  endDate: Date | null;
  onChangeSubscription: () => void;
  onOpenCalculator: () => void;
}

function SubscriptionStatusCard({
  businessSubscription,
  business,
  calculatedTotal,
  extraMembers,
  basePrice,
  variablePrice,
  endDate,
  onChangeSubscription,
  onOpenCalculator,
}: SubscriptionStatusCardProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.manageBusinessSubscription' });
  const { t: subT } = useTranslation('', { keyPrefix: 'page.subscriptions' });

  const isTrialActive = !!business?.trialEndDate && new Date(business.trialEndDate) > new Date();
  const hasSub = !!business?.subscription?.type;
  const isCancelled = business?.subscription?.willRenew === false;
  const isActive = business?.subscription?.willRenew === true;

  return (
    <Card className="flex items-stretch">
      <div className="flex-1">
        <h3 className="mb-4 text-lg font-semibold text-primary-900">{t('yourSubscription')}</h3>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-bold text-primary-900">
              {subT(`${businessSubscription.toLowerCase()}.title`)}
            </h2>
            {isTrialActive && !hasSub && (
              <span className="rounded-full bg-primary-300 px-3 py-1 text-sm font-semibold text-primary-900">
                {t('freeTrial')}
              </span>
            )}
            {isCancelled && (
              <span className="rounded-full bg-secondary px-3 py-1 text-sm font-semibold text-secondary-50">
                {t('cancelled')}
              </span>
            )}
            {isActive && (
              <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                {t('active')}
              </span>
            )}
          </div>

          {isCancelled && endDate && (
            <div className="flex items-start gap-2 rounded-lg border border-secondary px-3 py-2 text-sm font-medium text-secondary">
              <BiInfoCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('cancelledText', { date: formatDate(endDate) })}
            </div>
          )}

          {isActive && (
            <div className="flex flex-col gap-1 rounded-lg border border-primary-300 px-3 py-2 text-sm font-medium text-primary-900">
              <div className="flex items-center gap-2">
                <BiInfoCircle className="h-4 w-4 flex-shrink-0" />
                {t('nextPaymentText', { amount: calculatedTotal.toFixed(2) })}
              </div>
              {extraMembers > 0 && (
                <div className="ml-6 flex flex-col gap-0.5 text-xs text-primary-900 text-opacity-70">
                  <span>{t('breakdown.base', { amount: basePrice.toFixed(2) })}</span>
                  <span>
                    {t('breakdown.members', {
                      count: extraMembers,
                      price: (variablePrice * extraMembers).toFixed(2),
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="ml-6 flex flex-col justify-center gap-2">
        <Button variant="primary" onClick={onChangeSubscription}>
          <TbArrowsExchange className="h-4 w-4" />
          {t('changeSubscription')}
        </Button>
        <Button variant="secondary" onClick={onOpenCalculator}>
          <HiCalculator className="h-4 w-4" />
          {t('openCalculator')}
        </Button>
      </div>
    </Card>
  );
}

interface PriceBreakdownCardProps {
  businessSubscription: ReturnType<typeof useActiveBusinessSubscription>;
  memberCount: number;
  extraMembers: number;
  basePrice: number;
  variablePrice: number;
  paidSubCommunities: MemberCommunityDto[];
  calculatedTotal: number;
  loading: boolean;
}

function PriceBreakdownCard({
  businessSubscription,
  memberCount,
  extraMembers,
  basePrice,
  variablePrice,
  paidSubCommunities,
  calculatedTotal,
  loading,
}: PriceBreakdownCardProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.manageBusinessSubscription' });
  const { t: subT } = useTranslation('', { keyPrefix: 'page.subscriptions' });
  const { t: onboardT } = useTranslation('', { keyPrefix: 'page.onboarding' });

  return (
    <Card>
      <h3 className="mb-1 text-lg font-semibold text-primary-900">{t('nextPaymentDue')}</h3>
      <p className="mb-4 text-sm text-primary-900 text-opacity-60">{t('nextPaymentHelper')}</p>

      {loading ? (
        <div className="h-20 w-full animate-pulse rounded-xl bg-secondary-200" />
      ) : (
        <div className="rounded-xl border border-primary-300 p-4">
          <div className="flex flex-col gap-2">
            <PriceRow
              label={`${subT(`${businessSubscription.toLowerCase()}.title`)} (${t('basePlan')})`}
              value={`€${basePrice.toFixed(2)}`}
            />

            <div className="flex items-center justify-between text-sm text-primary-900">
              <div className="flex flex-col gap-0.5">
                <span className="text-primary-900 text-opacity-60">
                  {t('membersLabel')} ({memberCount} {t('members')})
                </span>
                <span className="text-xs text-primary-900 text-opacity-40">{t('membersIncludedNote')}</span>
              </div>
              <span className="font-medium">
                {extraMembers > 0 ? `€${(variablePrice * extraMembers).toFixed(2)}` : t('included')}
              </span>
            </div>
            {extraMembers > 0 && (
              <p className="text-xs text-primary-900 text-opacity-50">
                {t('breakdown.members', {
                  count: extraMembers,
                  price: (variablePrice * extraMembers).toFixed(2),
                })}
              </p>
            )}

            {paidSubCommunities.length === 0 ? (
              <PriceRow label={t('subCommunitiesLabel')} value="€0.00" />
            ) : (
              paidSubCommunities.map((c) => {
                const tier = c.subscription?.type ?? c.trialType!;
                return (
                  <div key={c.id} className="flex items-center justify-between text-sm text-primary-900">
                    <span className="text-primary-900 text-opacity-60">
                      {c.name} ({onboardT(`subscription.${tier.toLowerCase()}.title`)})
                      {!c.subscription?.active && (
                        <span className="ml-1 text-xs text-primary-900 text-opacity-40">
                          ({t('trial')})
                        </span>
                      )}
                    </span>
                    <span className="font-medium">
                      €{((communitySubscriptionPrices as Record<string, number>)[tier] ?? 0).toFixed(2)}
                    </span>
                  </div>
                );
              })
            )}

            <div className="mt-1 flex items-center justify-between border-t border-primary-300 pt-3">
              <span className="font-semibold text-primary-900">{t('total')}</span>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary-900">
                  €{calculatedTotal.toFixed(2)}
                </p>
                <p className="text-xs text-primary-900 text-opacity-50">
                  {onboardT('subscription.perMonth')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function PriceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm text-primary-900">
      <span className="text-primary-900 text-opacity-60">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

interface PaymentMethodCardProps {
  business: ReturnType<typeof useSelectedBusiness>;
  businessSubscription: ReturnType<typeof useActiveBusinessSubscription>;
  paymentDetails: PaymentDetails | null | undefined;
}

function PaymentMethodCard({ business, businessSubscription, paymentDetails }: PaymentMethodCardProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.manageBusinessSubscription' });
  const navigate = useNavigate();

  const isTrialActive = business?.trialEndDate ? new Date(business.trialEndDate) > new Date() : false;
  const hasSub = Boolean(business?.subscription?.type);
  const hasSubscription = Boolean(business?.subscription);

  const navigateToUpgrade = (changePaymentMode: boolean) =>
    navigate('/subscription/upgrade', {
      state: { changePaymentMode, subscriptionType: businessSubscription },
    });

  return (
    <Card>
      <h3 className="mb-2 text-lg font-semibold text-primary-900">{t('paymentMethod')}</h3>
      {isTrialActive && !hasSub ? (
        <div className="mb-4 rounded-lg bg-secondary p-4">
          <p className="text-sm font-medium text-secondary-50">{t('trialPaymentRequired')}</p>
          <p className="mt-1 text-sm text-secondary-50">{t('trialPaymentHelper')}</p>
        </div>
      ) : (
        <p className="mb-4 text-sm text-primary-900 text-opacity-60">{t('paymentMethodHelper')}</p>
      )}

      {paymentDetails === undefined ? (
        <div className="h-16 w-full animate-pulse rounded-xl bg-secondary-200" />
      ) : (
        <div className="flex flex-col gap-4 rounded-xl border border-primary-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            {paymentDetails ? (
              <PaymentMethodIcon details={paymentDetails} t={t} />
            ) : (
              <>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-300 bg-opacity-20">
                  <TbCreditCard size={20} className="text-primary-900" />
                </div>
                <p className="font-medium text-primary-900">
                  {isTrialActive ? t('setupPaymentMethodForTrial') : t('noPaymentMethod')}
                </p>
              </>
            )}
          </div>
          <Button
            disabled={!hasSubscription && !business?.trialEndDate}
            variant="primary"
            onClick={() => navigateToUpgrade(!(isTrialActive && !paymentDetails))}>
            {isTrialActive && !paymentDetails ? t('setupPaymentMethod') : t('editPaymentMethod')}
          </Button>
        </div>
      )}
    </Card>
  );
}

function InvoicesCard({ invoices, loading }: { invoices: InvoiceData[]; loading: boolean }) {
  const { t } = useTranslation('', { keyPrefix: 'page.manageBusinessSubscription' });
  const navigate = useNavigate();

  return (
    <Card>
      <h3 className="mb-1 text-lg font-semibold text-primary-900">{t('lastPayments')}</h3>
      <p className="mb-4 text-sm text-primary-900 text-opacity-60">{t('lastPaymentsHelper')}</p>
      <InvoicesTable invoices={invoices} loading={loading} />
      <div className="mt-4 flex justify-end">
        <Button variant="primary" onClick={() => navigate('/subscription/invoices')}>
          {t('showAllInvoices')}
        </Button>
      </div>
    </Card>
  );
}

interface SubscriptionActionsCardProps {
  isCancelled: boolean;
  actionLoading: boolean;
  onCancel: () => void;
  onReactivate: () => void;
}

function SubscriptionActionsCard({ isCancelled, actionLoading, onCancel, onReactivate }: SubscriptionActionsCardProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.manageBusinessSubscription' });

  return (
    <Card>
      <h3 className="mb-2 text-lg font-semibold text-primary-900">{t('subscriptionActions')}</h3>
      <p className="mb-4 text-sm text-primary-900 text-opacity-60">
        {isCancelled ? t('reactivateDescription') : t('cancelDescription')}
      </p>
      {isCancelled ? (
        <Button variant="primary" loading={actionLoading} onClick={onReactivate}>
          <TbHeart className="h-4 w-4" />
          {t('reactivate')}
        </Button>
      ) : (
        <Button variant="danger" loading={actionLoading} onClick={onCancel}>
          <TbHeartBroken className="h-4 w-4" />
          {t('cancel')}
        </Button>
      )}
    </Card>
  );
}

export default function ManageBusinessSubscriptionPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.manageBusinessSubscription' });
  const { t: subT } = useTranslation('', { keyPrefix: 'page.subscriptions' });
  const navigate = useNavigate();

  const businessSubscription = useActiveBusinessSubscription();
  const business = useSelectedBusiness();

  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [overrideEndDate, setOverrideEndDate] = useState<Date | null>(null);

  const dep = [businessSubscription, business?.subscription] as const;
  const { data, loading } = useSubscriptionPageData(dep);

  useEffect(() => {
    businessService.fetchBusinesses();
  }, []);

  const isCancelled = business?.subscription?.willRenew === false;
  const isActive = business?.subscription?.willRenew === true;

  const basePrice = (businessBaseSubscriptionPrices as Record<string, number>)[businessSubscription] ?? 0;
  const variablePrice = (businessVariableSubscriptionPrices as Record<string, number>)[businessSubscription] ?? 0;
  const memberCount = data?.memberCount ?? 0;
  const extraMembers = Math.max(0, memberCount - FREE_MEMBERS_INCLUDED);
  const paidSubCommunities = getPaidSubCommunities(data?.subCommunities ?? []);
  const subCommunityTotal = paidSubCommunities.reduce(
    (sum, c) => sum + ((communitySubscriptionPrices as Record<string, number>)[c.subscription?.type ?? c.trialType!] ?? 0),
    0,
  );
  const calculatedTotal = basePrice + variablePrice * extraMembers + subCommunityTotal;
  const endDate = overrideEndDate ?? data?.endDate ?? null;

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await businessSubscriptionService.cancelSubscription();
      await businessService.fetchBusinesses();
      const end = await businessSubscriptionService.getEndDate();
      if (end != null) {
        setOverrideEndDate(new Date(end * 1000));
      } else {
        setOverrideEndDate(null);
      }
    } finally {
      setActionLoading(false);
      setCancelModalOpen(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await businessSubscriptionService.reactivateSubscription();
      await businessService.fetchBusinesses();
    } catch (e: any) {
      if (e?.status === 402) {
        navigate('/subscription/upgrade', {
          state: { changePaymentMode: true, subscriptionType: businessSubscription, isRenewal: true },
        });
      }
    } finally {
      setActionLoading(false);
      setReactivateModalOpen(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        badge={
          <span className="rounded-xl border-2 border-primary-900 px-4 py-0.5 text-lg font-semibold uppercase">
            {subT(`${businessSubscription.toLowerCase()}.title`)}
          </span>
        }
      />

      {business && shouldShowTrialBanner(business) && (
        <TrialBanner
          trialEndDate={new Date(business.trialEndDate!)}
          plan={business.trialType!}
          onPaymentSetup={() =>
            navigate('/subscription/upgrade', {
              state: { changePaymentMode: false, subscriptionType: businessSubscription },
            })
          }
        />
      )}

      <div className="flex flex-col gap-6">
        <SubscriptionStatusCard
          businessSubscription={businessSubscription}
          business={business}
          calculatedTotal={calculatedTotal}
          extraMembers={extraMembers}
          basePrice={basePrice}
          variablePrice={variablePrice}
          endDate={endDate}
          onChangeSubscription={() =>
            navigate('/settings/subscriptions', {
              state: { changePaymentMode: false, subscriptionType: businessSubscription },
            })
          }
          onOpenCalculator={() => setCalculatorOpen(true)}
        />

        <PriceBreakdownCard
          businessSubscription={businessSubscription}
          memberCount={memberCount}
          extraMembers={extraMembers}
          basePrice={basePrice}
          variablePrice={variablePrice}
          paidSubCommunities={paidSubCommunities}
          calculatedTotal={calculatedTotal}
          loading={loading}
        />

        <PaymentMethodCard
          business={business}
          businessSubscription={businessSubscription}
          paymentDetails={loading ? undefined : (data?.paymentDetails ?? null)}
        />

        <InvoicesCard invoices={data?.invoices ?? []} loading={loading} />

        {(isActive || isCancelled) && (
          <SubscriptionActionsCard
            isCancelled={isCancelled}
            actionLoading={actionLoading}
            onCancel={() => setCancelModalOpen(true)}
            onReactivate={() => setReactivateModalOpen(true)}
          />
        )}
      </div>

      <CostCalculatorModal
        open={calculatorOpen}
        setOpen={setCalculatorOpen}
        initialSubscription={businessSubscription}
      />

      <AreYouSureModal
        open={cancelModalOpen}
        setOpen={setCancelModalOpen}
        title={t('cancelModal.title')}
        description={t('cancelModal.description')}
        confirmLabel={t('cancelModal.confirm')}
        cancelLabel={t('cancelModal.cancel')}
        onConfirm={handleCancel}
        onCancel={() => setCancelModalOpen(false)}
      />

      <AreYouSureModal
        open={reactivateModalOpen}
        setOpen={setReactivateModalOpen}
        title={t('reactivateModal.title')}
        description={t('reactivateModal.description')}
        confirmLabel={t('reactivateModal.confirm')}
        cancelLabel={t('reactivateModal.cancel')}
        onConfirm={handleReactivate}
        onCancel={() => setReactivateModalOpen(false)}
      />
    </>
  );
}
